import Client, { SupportedPlatformVariant } from '@wildix/xbees-connect';
import type { ConversationContext, Mode } from '../types/xbees';

// The one place that talks to the x-Bees SDK. Everything else asks this module.

export const xbees = (): ReturnType<typeof Client.getInstance> => Client.getInstance();

export function detectMode(c = xbees()): Mode {
  if (!c.isOpenedFromXBees()) return 'standalone';
  if (c.isActivationOnly()) return 'activation';
  if (c.isDataOnly()) return 'background';
  if (c.isFullsize()) return 'tab';
  return 'info';
}

/** SDK requests never resolve outside x-Bees; give up after `ms` instead of hanging. */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([promise, new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms))]);
}

/** ready("web") (D11). Must be followed promptly by reportAuthorized(). */
export async function ready(): Promise<void> {
  if (xbees().isOpenedFromXBees()) await withTimeout(xbees().ready(SupportedPlatformVariant.WEB), 5000);
}

export async function reportAuthorized(authorized: boolean): Promise<void> {
  if (!xbees().isOpenedFromXBees()) return;
  await withTimeout(authorized ? xbees().isAuthorized() : xbees().isNotAuthorized(), 5000);
}

/**
 * The contact in view. In standalone dev, ?phone=+1…&email=… stand in for x-Bees' context so the
 * UI can be exercised in a normal browser.
 */
export async function getConversationContext(): Promise<ConversationContext | null> {
  if (!xbees().isOpenedFromXBees()) {
    const q = new URLSearchParams(location.search);
    const phone = q.get('phone') ?? undefined;
    const email = q.get('email') ?? undefined;
    return phone || email ? { phone, email, contactId: 'dev', conversationId: 'dev' } : null;
  }
  const res = await withTimeout(xbees().getContext(), 5000);
  const payload = res?.payload;
  if (!payload) return null;
  return {
    contactId: payload.contact?.id,
    phone: payload.contact?.phone || undefined,
    email: payload.contact?.email || undefined,
    conversationId: payload.conversation?.id,
  };
}

/**
 * The agent's x-Bees token (Cognito JWT, rotates hourly; needs "Use x-bees authorization token" on
 * the integration). Read it per request and never store it (Invariant 9).
 */
export async function getXBeesToken(): Promise<string | null> {
  if (!xbees().isOpenedFromXBees()) return null;
  const res = await withTimeout(xbees().getXBeesToken(), 4000);
  const token = res?.payload;
  return typeof token === 'string' && token.split('.').length === 3 ? token : null;
}

export function onLogout(callback: () => void): void {
  if (xbees().isOpenedFromXBees()) xbees().onLogout(callback);
}

export async function showToast(message: string): Promise<void> {
  if (xbees().isOpenedFromXBees()) await withTimeout(xbees().showToast(message, 'INFO'), 4000);
}

/** This integration's full-screen Tab view in x-Bees (captured: <referrer>/integration/<id>). */
export function tabViewUrl(): string | null {
  const c = xbees();
  if (!c.isOpenedFromXBees() || !c.getIntegrationId()) return null;
  return `${c.getReferrer().replace(/\/$/, '')}/integration/${c.getIntegrationId()}`;
}

/** Opens the Tab view in the same x-Bees window; a new tab if the frame may not navigate it. */
export function openTabView(): void {
  const url = tabViewUrl();
  if (!url) return;
  try {
    if (window.open(url, '_top')) return;
  } catch {
    // sandboxed frame: fall through
  }
  window.open(url, '_blank', 'noopener');
}

export function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener');
}
